import { WebhookDispatch } from "@/@types/WebhookDispatch";
import BadgeShared from "@/components/shared/Badge/Badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Eye,
  Loader2,
  RefreshCw,
  XCircle,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import WebhooksCircle from "@/assets/icons/WebhooksCircle";

interface WebhooksListProps {
  webhooks: WebhookDispatch[];
  onViewDetails: (webhook: WebhookDispatch) => void;
  onRetry?: (webhookId: string) => void;
  retryingWebhookId?: string | null;
  className?: string;
  collapsible?: boolean;
}

const getStatusIcon = (status: string) => {
  const iconProps = { size: 14 };

  switch (status) {
    case "success":
      return <CheckCircle2 {...iconProps} className="text-[#A9D342]" />;
    case "failed":
      return <XCircle {...iconProps} className="text-[#F87171]" />;
    case "pending":
      return <Clock {...iconProps} className="text-[#FACC15]" />;
    case "processing":
      return <Loader2 {...iconProps} className="text-[#60A5FA] animate-spin" />;
    case "retrying":
      return <RefreshCw {...iconProps} className="text-[#FACC15] animate-spin" />;
    default:
      return <AlertTriangle {...iconProps} className="text-[#A1A1AA]" />;
  }
};

const getHttpStatusStyle = (httpStatus: number | null) => {
  if (!httpStatus) return "bg-zinc-800/50 text-zinc-400";

  if (httpStatus >= 200 && httpStatus < 300) {
    return "bg-[#A9D342]/10 text-[#A9D342] border border-[#A9D342]/20";
  }
  if (httpStatus >= 400 && httpStatus < 500) {
    return "bg-[#FACC15]/10 text-[#FACC15] border border-[#FACC15]/20";
  }
  return "bg-[#F87171]/10 text-[#F87171] border border-[#F87171]/20";
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

const contentVariants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2 },
    },
  },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2, delay: 0.1 },
    },
  },
};

export default function WebhooksList({
  webhooks,
  onViewDetails,
  onRetry,
  retryingWebhookId,
  className,
  collapsible = false,
}: WebhooksListProps) {
  const { t } = useTranslation(["sales", "common"]);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!webhooks || webhooks.length === 0) {
    return null;
  }

  const successCount = webhooks.filter((w) => w.status === "success").length;
  const failedCount = webhooks.filter((w) => w.status === "failed").length;

  return (
    <Card className={cn("p-5 md:p-6", className)}>
      <div
        className={cn(
          "flex items-center justify-between",
          collapsible ? "cursor-pointer select-none" : "mb-5",
          collapsible && !isExpanded && "mb-0",
          collapsible && isExpanded && "mb-5"
        )}
        onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="flex items-center gap-2">
          <WebhooksCircle />
          <h2 className="text-colorText font-semibold text-base">
            {t("common:webhooks_section")}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {collapsible && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mr-1">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A9D342]" />
                {successCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F87171]" />
                {failedCount}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Zap size={12} className="text-[#A9D342]" />
            <span>{webhooks.length}</span>
          </div>
          {collapsible && (
            <ChevronDown
              size={16}
              className={cn(
                "text-muted-foreground transition-transform duration-300",
                isExpanded && "rotate-180"
              )}
            />
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {(!collapsible || isExpanded) && (
          <motion.div
            initial={collapsible ? "collapsed" : false}
            animate="expanded"
            exit="collapsed"
            variants={contentVariants}
            className="overflow-hidden"
          >
            <motion.div
              className="space-y-2.5"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {webhooks.map((webhook) => {
                const isRetrying = retryingWebhookId === webhook.id;

                return (
                  <motion.div key={webhook.id} variants={itemVariants}>
                    <div
                      className={cn(
                        "relative p-3.5 rounded-xl border border-zinc-800/80",
                        "bg-gradient-to-br from-zinc-900/50 to-zinc-950/50",
                        "group transition-all duration-200",
                        "hover:border-zinc-700/80 hover:shadow-lg hover:shadow-black/20",
                        isRetrying && "opacity-70 pointer-events-none"
                      )}
                    >
                      <div
                        className={cn(
                          "absolute left-0 top-3 bottom-3 w-0.5 rounded-full transition-all",
                          webhook.status === "success" && "bg-[#A9D342]",
                          webhook.status === "failed" && "bg-[#F87171]",
                          webhook.status === "pending" && "bg-[#FACC15]",
                          webhook.status === "processing" && "bg-[#60A5FA]",
                          webhook.status === "retrying" && "bg-[#FACC15]",
                          !["success", "failed", "pending", "processing", "retrying"].includes(
                            webhook.status
                          ) && "bg-zinc-600"
                        )}
                      />

                      <div className="flex items-start justify-between gap-3 pl-2">
                        <div className="flex flex-col gap-2 min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(webhook.status)}
                            <span className="text-sm font-medium text-colorText truncate group-hover:text-white transition-colors">
                              {webhook.event}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            <BadgeShared status={webhook.status} />

                            {webhook.http_status && (
                              <span
                                className={cn(
                                  "text-xs font-mono px-2 py-0.5 rounded-md",
                                  getHttpStatusStyle(webhook.http_status)
                                )}
                              >
                                {webhook.http_status}
                              </span>
                            )}

                            {webhook.attempts > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <RefreshCw size={10} />
                                      {webhook.attempts}/{webhook.max_attempts}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      {t("common:attempts_info", {
                                        current: webhook.attempts,
                                        max: webhook.max_attempts,
                                      })}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>

                          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground/70">
                            {webhook.sent_at_formatted && (
                              <span className="flex items-center gap-1">
                                <Clock size={10} />
                                {webhook.sent_at_formatted}
                              </span>
                            )}

                            {webhook.integration && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800/60 hover:bg-zinc-800 transition-colors cursor-default max-w-[250px] lg:max-w-[200px]">
                                      <Zap size={10} className="text-primary shrink-0" />
                                      <span className="truncate">
                                        <span className="capitalize">{webhook.integration.integration?.replace(/_/g, " ")}</span> - {webhook.integration.name}
                                      </span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="max-w-[200px]">
                                    <p className="font-medium">{webhook.integration.name}</p>
                                    <p className="text-muted-foreground text-xs capitalize">
                                      {webhook.integration.integration?.replace(/_/g, " ")}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          {webhook.available_to_retry && onRetry && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                      "h-8 w-8 rounded-lg",
                                      "text-muted-foreground hover:text-[#A9D342]",
                                      "hover:bg-[#A9D342]/10",
                                      "transition-all duration-200"
                                    )}
                                    onClick={() => onRetry(webhook.id)}
                                    disabled={isRetrying}
                                  >
                                    {isRetrying ? (
                                      <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                      <RefreshCw size={14} />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t("common:retry")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-8 w-8 rounded-lg",
                                    "text-muted-foreground hover:text-white",
                                    "hover:bg-zinc-800",
                                    "transition-all duration-200"
                                  )}
                                  onClick={() => onViewDetails(webhook)}
                                >
                                  <Eye size={14} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t("common:details")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {!collapsible && (
              <div className="mt-4 pt-4 border-t border-zinc-800/50">
                <div className="flex items-center flex-wrap gap-2 sm:gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A9D342]" />
                    {successCount} {t("common:success")}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F87171]" />
                    {failedCount} {t("common:failed")}
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
